import React from 'react';
import { Box, Typography } from '@mui/material';
import { MEDALS, MedalKey, PLATINUM_START, TierHistoryPoint } from './scorecardData';
import DosenTooltip from '../ui/DosenTooltip';
import { isCapabilityRelease } from '../../utils/capabilityRelease';

/** Outlined tier badge — same rectangle as the measure rows' tier pill. */
const TierBadge: React.FC<{ medalKey: MedalKey }> = ({ medalKey }) => {
  const m = MEDALS[medalKey];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 58,
        px: '6px',
        py: '4px',
        borderRadius: '4px',
        bgcolor: `${m.color}1F`,
        border: `1px solid ${m.color}`,
        color: m.color,
        fontFamily: 'Inter, sans-serif',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.4px',
        lineHeight: 1,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap'
      }}
    >
      {m.label}
    </Box>
  );
};

/**
 * 6 month view of the tier: one point per week, read against the same tier
 * bands as the measures (stacked vertically), so the height of the line is the
 * tier. Ported from Ronan's #570 (reverted to rework the learner panel).
 *
 * It always renders, for every organization and learner:
 *  - The axis starts at the month tracking began and runs 6 months forward.
 *    Once that start is more than 5 months back the window rolls, so the
 *    current month is always on the axis and history is never dropped early.
 *  - This week's point is the live tier, so it always matches the badge.
 *  - A past week with no saved snapshot counts as Rust.
 *  - Weeks still to come are on the axis but carry no line.
 *
 * Nothing here invents a score: placeholder numbers once shipped as real org
 * data (RDP, Sep 2026).
 */

const BRAND_LIGHT = '#F79420';
const TEXT_TERTIARY = '#868686';
const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';

const WINDOW_MONTHS = 6;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Where a tier plots when all that is known is the tier: the middle of its band. */
const bandMid = (key: MedalKey, showPlatinum: boolean): number => {
  switch (key) {
    case 'platinum':
      return 95;
    case 'gold':
      return showPlatinum ? 82.5 : 87.5;
    case 'silver':
      return 62.5;
    case 'bronze':
      return 37.5;
    default:
      return 12.5;
  }
};

/** Which tier a position on the track falls in. */
const tierForScore = (score: number, showPlatinum: boolean): MedalKey => {
  if (showPlatinum && score >= PLATINUM_START) return 'platinum';
  if (score >= 75) return 'gold';
  if (score >= 50) return 'silver';
  if (score >= 25) return 'bronze';
  return 'wood';
};

// Weeks run Monday to Sunday in UTC, the boundary the BE snapshots on, so a
// snapshot's weekStart lands on exactly the slot it belongs to.
const utcWeekStart = (d: Date): Date => {
  const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const sinceMonday = (day.getUTCDay() + 6) % 7;
  return new Date(day.getTime() - sinceMonday * DAY_MS);
};
const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const monthIndex = (d: Date): number => d.getUTCFullYear() * 12 + d.getUTCMonth();
const monthStart = (index: number): Date => new Date(Date.UTC(Math.floor(index / 12), index % 12, 1));

export interface TrendWindow {
  /** Monday of every week on the axis, oldest first. */
  weeks: Date[];
  /** Plotted position per week; null for weeks still to come. */
  scores: Array<number | null>;
  /** First day of each of the 6 months on the axis. */
  months: Date[];
  /** Index of this week in `weeks`. */
  currentIndex: number;
}

/** The weeks on the axis and what each one plots. Pure, and takes `now`. */
export const buildTrendWindow = (
  history: TierHistoryPoint[],
  current: MedalKey | null,
  showPlatinum: boolean,
  now: Date = new Date()
): TrendWindow => {
  const thisWeek = utcWeekStart(now);
  const nowMonth = monthIndex(now);
  const firstSnapshot = history.length ? new Date(`${history[0].weekStart}T00:00:00Z`) : null;
  const firstMonth =
    firstSnapshot && !Number.isNaN(firstSnapshot.getTime()) ? Math.min(monthIndex(firstSnapshot), nowMonth) : nowMonth;
  // Start where tracking began, but never so far back that this month falls
  // off the right-hand end of the 6.
  const startMonth = Math.max(firstMonth, nowMonth - (WINDOW_MONTHS - 1));
  const months = Array.from({ length: WINDOW_MONTHS }, (_, i) => monthStart(startMonth + i));
  const windowEnd = monthStart(startMonth + WINDOW_MONTHS);

  const weeks: Date[] = [];
  for (let w = utcWeekStart(months[0]); w < windowEnd; w = new Date(w.getTime() + 7 * DAY_MS)) {
    weeks.push(w);
  }

  const saved = new Map(history.map((p) => [p.weekStart, p.score]));
  const thisWeekKey = isoDay(thisWeek);
  const rust = bandMid('wood', showPlatinum);
  const scores = weeks.map((w) => {
    if (w.getTime() > thisWeek.getTime()) return null;
    if (isoDay(w) === thisWeekKey && current) return bandMid(current, showPlatinum);
    return saved.get(isoDay(w)) ?? rust;
  });
  const currentIndex = weeks.findIndex((w) => isoDay(w) === thisWeekKey);
  return { weeks, scores, months, currentIndex };
};

/** Catmull-Rom to cubic Bezier, so the weekly points read as one flowing curve. */
const smoothPath = (pts: Array<[number, number]>): string => {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0][0]} ${pts[0][1]}`;
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
};

interface Props {
  /** Saved weekly snapshots from the BE. Absent or empty is fine: past weeks read Rust. */
  history?: TierHistoryPoint[] | null;
  /** The live overall tier. This week's point uses it, so the graph matches the badge. */
  current: MedalKey | null;
  /** Platinum band on top (learner) or Gold as the ceiling (organization). */
  showPlatinum: boolean;
  /** Narrow variant for the dashboard side panel. */
  compact?: boolean;
  /** Hover content for the "now" tier badge on the graph (e.g. the admin's predicted outcomes table). */
  badgeTooltip?: React.ReactNode;
}

const TierTrend: React.FC<Props> = ({ history, current, showPlatinum, compact = false, badgeTooltip }) => {
  const { weeks, scores, months, currentIndex } = buildTrendWindow(history || [], current, showPlatinum);

  // viewBox units; the SVG scales to its container (width 100%, height auto).
  const W = compact ? 420 : 860;
  const H = compact ? 150 : 180;
  // Compact swaps the tier names for colour dots, so the plot takes that width.
  const padL = compact ? 22 : 60;
  const padR = compact ? 12 : 16;
  const padT = 12;
  // Compact renders its month labels as HTML under the SVG (so they match the
  // measure titles' px size whatever the panel width), so it needs no room here.
  const padB = compact ? 6 : 30;
  const plotL = padL;
  const plotR = W - padR;
  const plotT = padT;
  const plotB = H - padB;
  const plotW = plotR - plotL;
  const plotH = plotB - plotT;
  const bandFont = compact ? 11 : 9;
  const axisFont = compact ? 12 : 10;

  const bands: Array<{ key: MedalKey; from: number; to: number }> = [
    { key: 'wood', from: 0, to: 25 },
    { key: 'bronze', from: 25, to: 50 },
    { key: 'silver', from: 50, to: 75 },
    { key: 'gold', from: 75, to: showPlatinum ? PLATINUM_START : 100 },
    ...(showPlatinum ? [{ key: 'platinum' as MedalKey, from: PLATINUM_START, to: 100 }] : [])
  ];

  const lastWeek = weeks.length - 1;
  const xAt = (i: number) => plotL + (lastWeek <= 0 ? 0 : (i / lastWeek) * plotW);
  const span = weeks[lastWeek].getTime() - weeks[0].getTime();
  const xForDate = (d: Date) => {
    const f = span <= 0 ? 0 : (d.getTime() - weeks[0].getTime()) / span;
    return plotL + Math.max(0, Math.min(1, f)) * plotW;
  };
  const yAt = (score: number) => plotT + (1 - Math.max(0, Math.min(100, score)) / 100) * plotH;

  // Only weeks up to this one carry a line; the rest of the axis is still to come.
  const pts = scores
    .slice(0, currentIndex + 1)
    .map((s, i) => [xAt(i), yAt(s ?? bandMid('wood', showPlatinum))] as [number, number]);
  const n = pts.length;
  const linePath = n >= 2 ? smoothPath(pts) : '';
  const areaPath = n >= 2 ? `${linePath} L ${pts[n - 1][0]} ${plotB} L ${pts[0][0]} ${plotB} Z` : '';

  const endScore = scores[currentIndex] ?? bandMid('wood', showPlatinum);
  const currentKey: MedalKey = current ?? tierForScore(endScore, showPlatinum);
  const endColor = MEDALS[currentKey].color;
  const [endX, endY] = pts[n - 1];
  // "Now" label, outlined as a tier badge for prominence (the panel no longer
  // repeats the tier under its title). SVG can't measure text, so the badge
  // width is estimated from the label length (bold uppercase Inter ≈ 0.72em).
  const nowLabel = MEDALS[currentKey].label.toUpperCase();
  const badgePadX = 8;
  const badgeW = nowLabel.length * axisFont * 0.72 + badgePadX * 2;
  const badgeH = axisFont + 10;
  const labelAnchor = endX < plotL + badgeW ? 'start' : 'end';
  const badgeX = labelAnchor === 'start' ? endX - 6 : endX - badgeW + 6;
  // Above the dot, or below it when there isn't room at the top.
  const aboveCenter = endY - 22;
  const labelY = aboveCenter - badgeH / 2 < plotT ? endY + 22 : aboveCenter;

  return (
    <Box
      sx={{
        flexShrink: 0,
        p: compact ? '8px 10px' : '10px 14px',
        borderRadius: compact ? '6px' : '8px',
        // Compact (tier panel): the faint brandy frame + tint shared with the pillar title boxes.
        border: compact ? '1px solid rgba(247, 148, 32, 0.35)' : `1px solid ${BORDER_DIM}`,
        bgcolor: compact ? 'rgba(247, 148, 32, 0.04)' : 'transparent'
      }}
    >
      {/* The dashboard side panel drops the title to save height. */}
      {!compact && (
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: BRAND_LIGHT,
            mb: '4px'
          }}
        >
          6 month view
        </Typography>
      )}

      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${isCapabilityRelease() ? 'Activity' : 'Tier'} by week over 6 months. This week: ${
          MEDALS[currentKey].label
        }.`}
        sx={{ display: 'block', width: '100%', height: 'auto' }}
      >
        <defs>
          <linearGradient id="tierTrendArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND_LIGHT} stopOpacity={0.3} />
            <stop offset="100%" stopColor={BRAND_LIGHT} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* Tier bands: the measure rows turned on their side. The current band is lit a little brighter. */}
        {bands.map((band) => {
          const yTop = yAt(band.to);
          const yBottom = yAt(band.from);
          return (
            <g key={band.key}>
              <rect
                x={plotL}
                y={yTop}
                width={plotW}
                height={yBottom - yTop}
                fill={MEDALS[band.key].color}
                opacity={band.key === currentKey ? 0.14 : 0.05}
              />
              {compact ? (
                // Colour dot per band; hovering it shows the tier badge.
                <DosenTooltip
                  title={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                      <TierBadge medalKey={band.key} />
                      {band.key === 'platinum' &&
                        `${MEDALS.platinum.label} is awarded to the top 10% of ${MEDALS.gold.label} learners.`}
                    </Box>
                  }
                  placement="right"
                >
                  <circle
                    cx={plotL - 11}
                    cy={(yTop + yBottom) / 2}
                    r={5}
                    fill={MEDALS[band.key].color}
                    opacity={band.key === currentKey ? 1 : 0.8}
                    style={{ cursor: 'default' }}
                  />
                </DosenTooltip>
              ) : (
                <text
                  x={plotL - 8}
                  y={(yTop + yBottom) / 2}
                  textAnchor="end"
                  dominantBaseline="central"
                  fontFamily="Inter, sans-serif"
                  fontSize={bandFont}
                  fontWeight={band.key === currentKey ? 700 : 500}
                  fill={MEDALS[band.key].color}
                  opacity={band.key === currentKey ? 1 : 0.75}
                >
                  {MEDALS[band.key].label}
                </text>
              )}
            </g>
          );
        })}

        {/* Band boundaries, plus a faint vertical guide at each month start. */}
        {bands.slice(1).map((band) => (
          <line
            key={`bound-${band.key}`}
            x1={plotL}
            x2={plotR}
            y1={yAt(band.from)}
            y2={yAt(band.from)}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        ))}
        {months.map((m) => (
          <line
            key={`grid-${isoDay(m)}`}
            x1={xForDate(m)}
            x2={xForDate(m)}
            y1={plotT}
            y2={plotB}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={1}
          />
        ))}

        {/* Area fill, then the line with a soft glow. A single week has no line yet, just its dot. */}
        {n >= 2 && (
          <>
            <path d={areaPath} fill="url(#tierTrendArea)" />
            <path
              d={linePath}
              fill="none"
              stroke={BRAND_LIGHT}
              strokeOpacity={0.28}
              strokeWidth={compact ? 5 : 6}
              strokeLinecap="round"
            />
            <path
              d={linePath}
              fill="none"
              stroke={BRAND_LIGHT}
              strokeWidth={compact ? 2 : 2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}

        {/* The six month labels. The axis itself is weekly underneath. */}
        {!compact &&
          months.map((m) => (
          <text
            key={`m-${isoDay(m)}`}
            x={xForDate(m)}
            y={H - 6}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize={axisFont}
            fill={TEXT_TERTIARY}
          >
            {m.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })}
          </text>
        ))}

        {/* "Now": this week, dotted in the live tier colour and labelled. */}
        <circle cx={endX} cy={endY} r={compact ? 8 : 9} fill={endColor} opacity={0.25} />
        <circle cx={endX} cy={endY} r={compact ? 5 : 5.5} fill={SURFACE} stroke={endColor} strokeWidth={2} />
        <circle cx={endX} cy={endY} r={compact ? 2.5 : 3} fill={endColor} />
        <DosenTooltip
          title={badgeTooltip ?? ''}
          placement="bottom"
          // No width cap — the content sets its own width (the outcomes table needs one line per cell).
          componentsProps={{ tooltip: { sx: { maxWidth: 'none', p: '12px 14px' } } }}
        >
          <g style={{ cursor: 'default' }}>
            <rect
              x={badgeX}
              y={labelY - badgeH / 2}
              width={badgeW}
              height={badgeH}
              rx={4}
              fill={SURFACE}
              stroke={endColor}
              strokeWidth={1.5}
            />
            <rect x={badgeX} y={labelY - badgeH / 2} width={badgeW} height={badgeH} rx={4} fill={endColor} opacity={0.12} />
            <text
              x={badgeX + badgeW / 2}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="Inter, sans-serif"
              fontSize={axisFont}
              fontWeight={700}
              letterSpacing="0.5px"
              fill={endColor}
            >
              {nowLabel}
            </text>
          </g>
        </DosenTooltip>
      </Box>

      {/* Compact month labels in HTML, sized to match the measure titles (12.5px). */}
      {compact && (
        <Box sx={{ position: 'relative', height: 18, mt: '2px' }}>
          {months.map((m) => (
            <Typography
              key={`m-${isoDay(m)}`}
              sx={{
                position: 'absolute',
                left: `${(xForDate(m) / W) * 100}%`,
                transform: 'translateX(-50%)',
                fontFamily: 'Inter, sans-serif',
                fontSize: 12.5,
                lineHeight: '18px',
                color: TEXT_TERTIARY,
                whiteSpace: 'nowrap'
              }}
            >
              {m.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TierTrend;
